import apiClient from './client';

/**
 * Response shape from `GET /billing/linkage-eligibility`.
 *
 * The frontend queries this to decide which "Link X" buttons to render
 * on the Home page and in `LinkAccountModal`. The backend OAuth-authorize
 * (Meta) and link-account (Google) endpoints apply the same check
 * server-side, so this is the single source of truth for eligibility —
 * do NOT derive it locally from `hasLinkedGoogleAccounts` /
 * `hasLinkedMetaAccounts` alone.
 *
 * `upgrade_required` is the signal for the Pro upsell nudge: true when a
 * free-tier member is blocked from linking the second platform because
 * they already linked one.
 */
export interface LinkageEligibility {
  can_link_google: boolean;
  can_link_meta: boolean;
  // DB-level linkage state (`has_linked_platform` — ignores `is_active`).
  // Distinct from `can_link_*` because those express *permission* to link
  // and Pro members are permitted regardless of existing linkages.
  // Callers that need to answer "does this member have any linkage?"
  // must read these fields, not invert `can_link_*`.
  has_linked_google: boolean;
  has_linked_meta: boolean;
  upgrade_required: boolean;
  reason: string | null;
}

export const billingApi = {
  /**
   * Return per-platform link eligibility for the current user.
   *
   * Cache under React Query key `['linkage-eligibility']` and invalidate
   * on: successful Google/Meta link, Google/Meta disconnect, plan
   * change (Pro upgrade / downgrade).
   */
  async getLinkageEligibility(): Promise<LinkageEligibility> {
    const response = await apiClient.get<LinkageEligibility>(
      '/billing/linkage-eligibility',
    );
    return response.data;
  },
};

export default billingApi;
